import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaKey, FaPlus, FaPen, FaArchive, FaEye, FaEyeSlash, FaFileExport, FaLockOpen, FaLock,
    FaChevronRight, FaChevronDown, FaFileAlt, FaFilePdf, FaDownload, FaTerminal,
    FaStar, FaRegStar, FaExpand, FaSitemap, FaTrash, FaSlidersH,
} from "react-icons/fa";
import { CREDENTIALS_CONSTANTS } from "../_MODULES/_API";
import { useProjectAccess } from "../_Hooks/useProjectAccess";
import { CopyValue } from "./CopyValue";
import {
    OPTIONS_CREDENTIAL_TYPE, OPTIONS_ENVIRONMENT_CODE, onFindOptionLabel,
    onCredentialFields, CREDENTIAL_FIELD_LABELS,
} from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { AppCodeBlock } from "../../../../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import {
    FormGrid, FormSection, FieldInput, FieldSelect, FieldTextarea, FieldSwitch,
    FieldFile, FieldCode, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { downloadCredentialTxt, downloadCredentialPdf, onStripHtml } from "../../../../_UI/CredentialDownload";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { NestedVaultModal } from "../../../../_UI/NestedVaultModal";
import {
    CredentialSpecializedSections, onEmptyCredentialTechnical, onSplitCredentialTechnicalExtra,
    onMergeCredentialTechnicalExtra,
} from "../../../../_UI/CredentialSpecializedSections";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import "../../../../_UI/_Styles/FileCollection.css";

const SECRET_FIELDS = ["secret", "token", "connectionString", "sshKey"];
const EMPTY_EXTRA = { label: "", value: "", isSecret: false };

const EMPTY_VALUES = {
    parentId: "", environmentId: "", title: "", type: "DATABASE", envKey: "", isFavorite: false,
    email: "", host: "", port: "", username: "", secret: "", passwordHint: "", token: "",
    databaseName: "", connectionString: "", sshKey: "", sshCommand: "",
    sshKeygenCommand: "", notes: "",
};

const FILE_PURPOSE_LABEL = {
    GENERAL: "Archivo",
    SSH_PUBLIC_KEY: "Clave pública SSH",
    SSH_PRIVATE_KEY: "Clave privada SSH",
};

const onBuildTree = (items = []) => {
    const map = new Map(items.map((item) => [String(item.id), { ...item, children: [] }]));
    const roots = [];
    map.forEach((item) => {
        const parentId = item.parentId ? String(item.parentId) : "";
        if (parentId && map.has(parentId) && parentId !== String(item.id)) map.get(parentId).children.push(item);
        else roots.push(item);
    });
    return roots;
};

const onFlattenTree = (items, level = 0) => items.flatMap((item) => [
    { ...item, level },
    ...onFlattenTree(item.children ?? [], level + 1),
]);


const onDownloadBlob = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = name || "archivo";
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const PanelCredentials = ({ project }) => {

    const projectId = project.id;
    const { useAccessPin } = useProjectAccess(projectId);
    const [URL_PARAMS] = useSearchParams();
    const FOCUS_CREDENTIAL_ID = URL_PARAMS.get("focusCredential") || "";

    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [useTypeFilter, setTypeFilter] = useState("ALL");
    const [useEnvironmentFilter, setEnvironmentFilter] = useState("ALL");
    const [useFavoriteFilter, setFavoriteFilter] = useState("ALL");
    const [useRevealed, setRevealed] = useState({});
    const [useExported, setExported] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useCredentialEdit, setCredentialEdit] = useState(null);
    const [useParentDefault, setParentDefault] = useState(null);
    const [useExtra, setExtra] = useState([]);
    const [useTechnical, setTechnical] = useState(onEmptyCredentialTechnical);
    const [usePublicKeyFiles, setPublicKeyFiles] = useState([]);
    const [usePrivateKeyFiles, setPrivateKeyFiles] = useState([]);
    const [useNestedCredential, setNestedCredential] = useState(null);
    const [useCardExpanded, setCardExpanded] = useState({});
    const [useExportModal, setExportModal] = useState(null);
    const [useExportSelection, setExportSelection] = useState({});
    const [usePreviewFile, setPreviewFile] = useState(null);
    const [useDownloadingFilesKey, setDownloadingFilesKey] = useState("");

    const { apiGet: onGetCredentials, useApiData: CREDENTIALS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadCredentials = () => onGetCredentials({
        endpoint: CREDENTIALS_CONSTANTS.BY_PROJECT, isDetail: true, config: { pathParams: { projectId } },
    });

    useEffect(() => { onLoadCredentials(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const ENVIRONMENTS = useMemo(() => (project.subProjects ?? []).flatMap((subProject) => (
        (subProject.environments ?? []).map((environment) => ({ ...environment, subProjectName: subProject.name }))
    )), [project.subProjects]);

    const SORTED_CREDENTIALS = useMemo(() => [...(CREDENTIALS ?? [])].sort((a, b) =>
        (Number(!!b.isFavorite) - Number(!!a.isFavorite))
        || (Number(a.position ?? 0) - Number(b.position ?? 0))
        || String(a.title ?? "").localeCompare(String(b.title ?? ""))
    ), [CREDENTIALS]);
    const TREE = useMemo(() => onBuildTree(SORTED_CREDENTIALS), [SORTED_CREDENTIALS]);
    const ALL_ITEMS = useMemo(() => onFlattenTree(TREE), [TREE]);
    const NESTED_CREDENTIAL = useMemo(() => {
        if (!useNestedCredential) return null;
        const id = typeof useNestedCredential === "object" ? useNestedCredential.id : useNestedCredential;
        return TREE.find((credential) => String(credential.id) === String(id)) ?? null;
    }, [TREE, useNestedCredential]);

    const onCredentialMatches = (credential) => onMatchesSearch(useSearch, [
        credential.title, credential.type, onFindOptionLabel(OPTIONS_CREDENTIAL_TYPE, credential.type),
        credential.envKey, credential.email, credential.username, credential.host, credential.port,
        credential.databaseName, credential.notes, credential.environment?.name,
        credential.sshCommand, credential.sshKeygenCommand,
        ...(credential.extra ?? []).flatMap((field) => [field.label, field.isSecret ? "" : field.value]),
        ...(credential.files ?? []).map((file) => file.originalName || file.title),
    ]);
    const onCredentialAdvancedMatches = (credential) => (
        (useTypeFilter === "ALL" || credential.type === useTypeFilter)
        && (useEnvironmentFilter === "ALL" || String(credential.environmentId || credential.environment?.id || "") === String(useEnvironmentFilter))
        && (useFavoriteFilter === "ALL" || (!!credential.isFavorite === (useFavoriteFilter === "YES")))
    );
    const ADVANCED_COUNT = [useTypeFilter !== "ALL", useEnvironmentFilter !== "ALL", useFavoriteFilter !== "ALL"].filter(Boolean).length;

    const SEARCH_ITEMS = useMemo(() => ALL_ITEMS.filter((credential) => onCredentialAdvancedMatches(credential) && onCredentialMatches(credential)),
        [ALL_ITEMS, useSearch, useTypeFilter, useEnvironmentFilter, useFavoriteFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const DISPLAY_ITEMS = useMemo(() => {
        const hasMatch = (credential) => (onCredentialAdvancedMatches(credential) && onCredentialMatches(credential))
            || (credential.children ?? []).some((child) => hasMatch(child));
        return TREE.filter((credential) => hasMatch(credential)).map((credential) => ({ ...credential, level: 0 }));
    }, [TREE, useSearch, useTypeFilter, useEnvironmentFilter, useFavoriteFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const PAGER = usePagedList(DISPLAY_ITEMS, 6, "credentialsPage");

    useEffect(() => {
        if (!FOCUS_CREDENTIAL_ID || IS_LOADING || !ALL_ITEMS.length) return;

        const target = ALL_ITEMS.find((credential) => String(credential.id) === String(FOCUS_CREDENTIAL_ID));
        if (!target) return;

        const root = TREE.find((credential) => (
            String(credential.id) === String(FOCUS_CREDENTIAL_ID)
            || onFlattenTree(credential.children ?? []).some((child) => String(child.id) === String(FOCUS_CREDENTIAL_ID))
        ));
        if (!root) return;

        if (String(target.id) !== String(root.id)) {
            if (String(useNestedCredential ?? "") !== String(root.id)) {
                setNestedCredential(root.id);
                return;
            }
        } else if (Number(PAGER.usePerPage) !== -1) {
            const rootIndex = DISPLAY_ITEMS.findIndex((credential) => String(credential.id) === String(root.id));
            if (rootIndex >= 0) {
                const targetPage = Math.floor(rootIndex / Number(PAGER.usePerPage)) + 1;
                if (PAGER.usePage !== targetPage) {
                    PAGER.onChangePage(targetPage);
                    return;
                }
            }
        }

        const timer = window.setTimeout(() => {
            const element = document.querySelector(`[data-credential-focus-id="${FOCUS_CREDENTIAL_ID}"]`);
            element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }, String(target.id) === String(root.id) ? 80 : 160);

        return () => window.clearTimeout(timer);
    }, [FOCUS_CREDENTIAL_ID, IS_LOADING, ALL_ITEMS, TREE, DISPLAY_ITEMS, PAGER.usePage, PAGER.usePerPage, useNestedCredential]); // eslint-disable-line react-hooks/exhaustive-deps

    const FORM = useForm({
        initialValues: EMPTY_VALUES,
        schema: { title: { required: true } },
        onSubmit: async (values) => {
            const IS_EDIT = !!useCredentialEdit?.id;
            const VISIBLE = onCredentialFields(values.type);
            const ENVIRONMENT_VALUE = String(values.environmentId || "");
            const IS_PRESET_ENVIRONMENT = ENVIRONMENT_VALUE.startsWith("preset:");
            const PAYLOAD = {
                parentId: values.parentId || null,
                title: values.title.trim(),
                type: values.type,
                envKey: values.envKey.trim(),
                environmentId: IS_PRESET_ENVIRONMENT ? null : (ENVIRONMENT_VALUE || null),
                environmentCode: IS_PRESET_ENVIRONMENT ? ENVIRONMENT_VALUE.slice(7) : null,
                isFavorite: !!values.isFavorite,
                notes: values.notes || "",
                sshCommand: values.sshCommand || "",
                sshKeygenCommand: values.sshKeygenCommand || "",
                extra: onMergeCredentialTechnicalExtra(
                    useExtra.filter((field) => !!field.label.trim()),
                    useTechnical,
                ),
                accessPin: useAccessPin ?? undefined,
            };

            if (!IS_EDIT) PAYLOAD.projectId = projectId;

            VISIBLE.forEach((field) => {
                const VALUE = values[field];
                if (VALUE) {
                    PAYLOAD[field] = VALUE;
                    return;
                }
                const IS_SECRET = SECRET_FIELDS.includes(field);
                const WAS_PREFILLED = IS_EDIT && (!IS_SECRET || !!useRevealed[useCredentialEdit.id]);
                if (WAS_PREFILLED) PAYLOAD[field] = "";
            });

            // sshKey no forma parte del catálogo de campos por tipo, pero sí es un secreto de credencial.
            if (values.sshKey) PAYLOAD.sshKey = values.sshKey;
            else if (IS_EDIT && useRevealed[useCredentialEdit.id]) PAYLOAD.sshKey = "";

            if (IS_EDIT) {
                Object.keys(CREDENTIAL_FIELD_LABELS).forEach((field) => {
                    if (!VISIBLE.includes(field)) PAYLOAD[field] = "";
                });
            }

            const RESPONSE = IS_EDIT
                ? await actionMutate({
                    method: "PATCH", endpoint: CREDENTIALS_CONSTANTS.UPDATE,
                    successMessage: "Credencial actualizada", id: useCredentialEdit.id, data: PAYLOAD,
                })
                : await actionMutate({
                    method: "POST", endpoint: CREDENTIALS_CONSTANTS.CREATE,
                    successMessage: "Credencial guardada", data: PAYLOAD,
                });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            const saved = RESPONSE.data;
            const credentialId = saved?.id ?? useCredentialEdit?.id;
            let filesOk = true;
            const nextFiles = [...(saved?.files ?? useCredentialEdit?.files ?? [])];

            if (credentialId && usePublicKeyFiles.length > 0) {
                const uploaded = await onUploadCredentialFiles(credentialId, usePublicKeyFiles, "SSH_PUBLIC_KEY");
                if (uploaded !== false) {
                    nextFiles.push(...uploaded);
                    setPublicKeyFiles([]);
                } else filesOk = false;
            }
            if (credentialId && usePrivateKeyFiles.length > 0) {
                const uploaded = await onUploadCredentialFiles(credentialId, usePrivateKeyFiles, "SSH_PRIVATE_KEY");
                if (uploaded !== false) {
                    nextFiles.push(...uploaded);
                    setPrivateKeyFiles([]);
                } else filesOk = false;
            }

            const localSaved = { ...(useCredentialEdit ?? {}), ...(saved ?? {}), files: nextFiles };
            if (credentialId) {
                if (IS_EDIT) mutateLocal({ id: credentialId, patch: localSaved });
                else mutateLocal({ insert: localSaved, position: "end" });
            }
            if (!IS_EDIT && localSaved.parentId && !useNestedCredential) {
                setNestedCredential(localSaved.parentId);
            }

            if (!filesOk) {
                setCredentialEdit(localSaved);
                return;
            }

            FORM.reset();
            setExtra([]);
            setTechnical(onEmptyCredentialTechnical());
            setCredentialEdit(null);
            setParentDefault(null);
            setIsModalOpen(false);
        },
    });

    const onUploadCredentialFiles = async (credentialId, files, purpose) => {
        if (!files.length) return [];
        const formData = new FormData();
        formData.append("purpose", purpose);
        if (useAccessPin) formData.append("accessPin", useAccessPin);
        files.forEach((file) => formData.append("files[]", file));
        const url = API_CORE.ENDPOINTS[CREDENTIALS_CONSTANTS.FILE_UPLOAD].replace(":id", credentialId);
        const response = await API_CORE.SERVICES.POST_RESOURCE(url, formData);
        if (response?.success === false) {
            REACT_HOT_TOAST_ERROR(response?.message || "No se pudo subir el archivo SSH.");
            return false;
        }
        return Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    };

    const onOpenCreate = (parentId = null) => {
        const requestedParent = parentId ? ALL_ITEMS.find((item) => String(item.id) === String(parentId)) : null;
        const normalizedParentId = requestedParent?.parentId ? requestedParent.parentId : parentId;

        setCredentialEdit(null);
        setParentDefault(normalizedParentId);
        setExtra([]);
        setTechnical(onEmptyCredentialTechnical());
        setPublicKeyFiles([]);
        setPrivateKeyFiles([]);
        FORM.setValues({ ...EMPTY_VALUES, parentId: normalizedParentId ?? "" });
        setIsModalOpen(true);
    };

    const onOpenEdit = (credential) => {
        const REVEALED = useRevealed[credential.id];
        setCredentialEdit(credential);
        setParentDefault(null);
        const SPLIT_EXTRA = onSplitCredentialTechnicalExtra(REVEALED?.extra ?? credential.extra ?? []);
        setExtra(SPLIT_EXTRA.custom);
        setTechnical(SPLIT_EXTRA.technical);
        setPublicKeyFiles([]);
        setPrivateKeyFiles([]);
        FORM.setValues({
            ...EMPTY_VALUES,
            parentId: credential.parentId ?? "",
            environmentId: credential.environmentId || (credential.environmentCode ? `preset:${credential.environmentCode}` : ""),
            title: credential.title ?? "",
            type: credential.type ?? "OTHER",
            envKey: credential.isEnvKeyDerived ? "" : (credential.envKey ?? ""),
            isFavorite: !!credential.isFavorite,
            email: credential.email ?? "",
            host: credential.host ?? "",
            port: credential.port ?? "",
            username: credential.username ?? "",
            databaseName: credential.databaseName ?? "",
            notes: credential.notes ?? "",
            sshCommand: credential.sshCommand ?? "",
            sshKeygenCommand: credential.sshKeygenCommand ?? "",
            secret: REVEALED?.secret ?? "",
            passwordHint: REVEALED?.passwordHint ?? credential.passwordHint ?? "",
            token: REVEALED?.token ?? "",
            connectionString: REVEALED?.connectionString ?? "",
            sshKey: REVEALED?.sshKey ?? "",
        });
        setIsModalOpen(true);
    };

    const onCloseModal = () => {
        setIsModalOpen(false);
        setCredentialEdit(null);
        setParentDefault(null);
        setExtra([]);
        setTechnical(onEmptyCredentialTechnical());
        setPublicKeyFiles([]);
        setPrivateKeyFiles([]);
    };

    const onRevealData = async (credential) => {
        if (useRevealed[credential.id]) return useRevealed[credential.id];
        const url = API_CORE.ENDPOINTS[CREDENTIALS_CONSTANTS.REVEAL].replace(":id", credential.id);
        const response = await API_CORE.SERVICES.POST_RESOURCE(url, { accessPin: useAccessPin ?? undefined });
        const data = response?.data ?? null;
        if (data) setRevealed((prev) => ({ ...prev, [credential.id]: data }));
        return data;
    };

    const onReveal = async (credential) => {
        if (useRevealed[credential.id]) {
            setRevealed((prev) => ({ ...prev, [credential.id]: null }));
            return;
        }
        await onRevealData(credential);
    };

    const onExportEnv = async (environmentId) => {
        const response = await API_CORE.SERVICES.POST_RESOURCE(
            API_CORE.ENDPOINTS[CREDENTIALS_CONSTANTS.EXPORT_ENV],
            { projectId, environmentId, accessPin: useAccessPin ?? undefined },
        );
        if (response?.success === false) {
            REACT_HOT_TOAST_ERROR(response?.message || "No se pudo exportar el entorno.");
            return;
        }
        setExported(response?.data?.content ?? "");
    };

    const onArchive = async (credential) => {
        const nested = onFlattenTree(credential.children ?? []);
        const IS_CONFIRMED = await confirm.danger(
            "Archivar credencial",
            nested.length
                ? `Se archiva "${credential.title}" junto con ${nested.length} credencial${nested.length === 1 ? " hija" : "es hijas"}. Podés restaurarlas desde Archivados.`
                : `Se archiva "${credential.title}". Podés restaurarla desde Archivados.`,
        );
        if (!IS_CONFIRMED) return;

        const previous = CREDENTIALS ?? [];
        const ids = new Set([String(credential.id), ...nested.map((item) => String(item.id))]);
        mutateLocal({ replaceAll: previous.filter((item) => !ids.has(String(item.id))) });
        if (!credential.parentId || String(useNestedCredential ?? "") === String(credential.id)) {
            setNestedCredential(null);
        }

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: CREDENTIALS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: credential.id }, data: { accessPin: useAccessPin ?? undefined },
            successMessage: "Credencial archivada",
        });
        if (!RESPONSE.success) mutateLocal({ replaceAll: previous });
    };

    const onLoadCredentialFileBlob = async (file) => {
        const url = API_CORE.ENDPOINTS[CREDENTIALS_CONSTANTS.FILE_DOWNLOAD].replace(":id", file.id);
        return API_CORE.SERVICES.POST_RESOURCE(
            url,
            { accessPin: useAccessPin ?? undefined },
            { responseType: "blob" },
        );
    };

    const onDownloadCredentialFile = async (file) => {
        const blob = await onLoadCredentialFileBlob(file);
        if (!(blob instanceof Blob)) {
            REACT_HOT_TOAST_ERROR("No se pudo descargar el archivo.");
            return;
        }
        onDownloadBlob(blob, file.originalName || file.title || "archivo");
    };

    const onDownloadCredentialFiles = async (credential) => {
        const files = credential?.files ?? [];
        const key = `credential:${credential?.id || "files"}`;
        if (!files.length || useDownloadingFilesKey) return;
        setDownloadingFilesKey(key);
        try {
            await downloadFilesAsZip({
                files,
                loadBlob: onLoadCredentialFileBlob,
                fileName: `${credential?.title || "credencial"}-archivos.zip`,
                successMessage: `${files.length} archivo${files.length === 1 ? "" : "s"} de la credencial descargado${files.length === 1 ? "" : "s"}`,
                errorMessage: "No se pudieron descargar todos los archivos de la credencial.",
            });
        } finally {
            setDownloadingFilesKey("");
        }
    };

    const onArchiveCredentialFile = async (file) => {
        const confirmed = await confirm.danger("Archivar archivo", `Se archiva "${file.originalName || file.title}". Podés restaurarlo desde Archivados.`);
        if (!confirmed) return;
        const owner = (CREDENTIALS ?? []).find((credential) => (credential.files ?? []).some((item) => item.id === file.id));
        if (owner) mutateLocal({ id: owner.id, patch: { files: (owner.files ?? []).filter((item) => item.id !== file.id) } });
        const response = await onApiAction({
            method: "PATCH", endpoint: CREDENTIALS_CONSTANTS.FILE_ARCHIVE,
            pathParams: { id: file.id }, data: { accessPin: useAccessPin ?? undefined },
            successMessage: "Archivo archivado",
        });
        if (!response.success && owner) mutateLocal({ id: owner.id, patch: { files: owner.files ?? [] } });
        if (response.success && usePreviewFile?.id === file.id) setPreviewFile(null);
    };

    const onChangeExtra = (index, field, value) => {
        setExtra((prev) => prev.map((row, position) => position === index ? { ...row, [field]: value } : row));
    };
    const onChangeTechnical = (field, value) => {
        setTechnical((prev) => ({ ...prev, [field]: value }));
    };
    const onAddExtra = () => setExtra((prev) => [...prev, { ...EMPTY_EXTRA }]);
    const onRemoveExtra = (index) => setExtra((prev) => prev.filter((_, position) => position !== index));

    const onToggleCardExpanded = (credentialId) => {
        setCardExpanded((prev) => ({ ...prev, [credentialId]: !prev[credentialId] }));
    };

    const onGenerateSshCommand = () => {
        const host = String(FORM.values.host || "").trim();
        if (!host) {
            FORM.setError("host", "Completá el host o IP para generar el comando.", { focus: true });
            return;
        }
        const username = String(FORM.values.username || "").trim();
        const port = String(FORM.values.port || "").trim();
        const target = `${username ? `${username}@` : ""}${host}`;
        FORM.setValue("sshCommand", `ssh${port && port !== "22" ? ` -p ${port}` : ""} ${target}`);
    };

    const onGenerateSshKeygen = () => {
        const slug = String(FORM.values.title || "notebook")
            .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
            .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "notebook";
        const comment = [FORM.values.username, FORM.values.host].filter(Boolean).join("@") || FORM.values.title || "notebook";
        FORM.setValue("sshKeygenCommand", `ssh-keygen -t ed25519 -a 64 -f ~/.ssh/id_${slug} -C "${String(comment).replace(/"/g, "")}"`);
    };

    const onRenderRow = (credential, field) => {
        const REVEALED = useRevealed[credential.id];
        const IS_SECRET = SECRET_FIELDS.includes(field);
        const HAS_SECRET = {
            secret: credential.hasSecret,
            token: credential.hasToken,
            connectionString: credential.hasConnectionString,
            sshKey: credential.hasSshKey,
        }[field];
        if (IS_SECRET && !HAS_SECRET) return null;
        if (!IS_SECRET && !credential[field]) return null;
        const VALUE = IS_SECRET ? (REVEALED ? REVEALED[field] : credential[field]) : credential[field];
        const CAN_COPY = !IS_SECRET || !!REVEALED;
        const label = CREDENTIAL_FIELD_LABELS[field] ?? (field === "sshKey" ? "Clave SSH" : field);
        return (
            <div key={field} className="LStudio__app_credentials_item_row">
                <span className="LStudio__app_credentials_item_key">{label}</span>
                {CAN_COPY
                    ? <CopyValue value={VALUE} label={`Copiar ${label}`} />
                    : <span className="LStudio__app_credentials_item_hidden">{VALUE}</span>}
            </div>
        );
    };

    const onCredentialRows = (credential, revealed) => [
        { key: "type", label: "Tipo", value: onFindOptionLabel(OPTIONS_CREDENTIAL_TYPE, credential.type), isSecret: false },
        { key: "environment", label: "Entorno", value: credential.environment ? `${credential.environment.subProjectName ? `${credential.environment.subProjectName} · ` : ""}${credential.environment.name}` : "", isSecret: false },
        { key: "envKey", label: "Prefijo .env", value: credential.envKey ?? "", isSecret: false },
        { key: "email", label: "Correo", value: credential.email ?? "", isSecret: false },
        { key: "username", label: "Usuario", value: credential.username ?? "", isSecret: false },
        { key: "host", label: "Host", value: credential.host ?? "", isSecret: false },
        { key: "port", label: "Puerto", value: credential.port ?? "", isSecret: false },
        { key: "secret", label: "Contraseña real", value: revealed?.secret ?? "", isSecret: true },
        { key: "passwordHint", label: "Pista / palabra secreta", value: revealed?.passwordHint ?? credential.passwordHint ?? "", isSecret: false },
        { key: "databaseName", label: "Base de datos", value: credential.databaseName ?? "", isSecret: false },
        { key: "token", label: "Token / API key / secreto", value: revealed?.token ?? "", isSecret: true },
        { key: "connectionString", label: "Cadena de conexión", value: revealed?.connectionString ?? "", isSecret: true },
        { key: "sshKey", label: "Clave SSH", value: revealed?.sshKey ?? "", isSecret: true },
        { key: "sshCommand", label: "Comando SSH", value: credential.sshCommand ?? "", isSecret: false },
        { key: "sshKeygenCommand", label: "Generador de clave SSH", value: credential.sshKeygenCommand ?? "", isSecret: false },
        ...(credential.extra ?? []).map((field, index) => ({
            key: `extra_${index}`,
            label: field.label,
            isSecret: !!field.isSecret,
            value: field.isSecret
                ? (revealed?.extra?.find((item) => item.label === field.label)?.value ?? "")
                : field.value,
        })),
        { key: "files", label: "Archivos", value: (credential.files ?? []).map((file) => `${FILE_PURPOSE_LABEL[file.purpose] || "Archivo"}: ${file.originalName || file.title}`).join(", "), isSecret: false },
        { key: "notes", label: "Notas", value: onStripHtml(credential.notes), isSecret: false },
    ].filter((row) => row.value !== null && row.value !== undefined && String(row.value) !== "");

    const onOpenExport = async (credential) => {
        const revealed = await onRevealData(credential);
        if (!revealed) return;
        const rows = onCredentialRows(credential, revealed);
        setExportSelection(Object.fromEntries(rows.map((row) => [row.key, !row.isSecret])));
        setExportModal({ credential, rows });
    };

    const onExportCredential = (format) => {
        if (!useExportModal) return;
        const rows = useExportModal.rows
            .filter((row) => !!useExportSelection[row.key])
            .map(({ label, value }) => ({ label, value }));
        if (!rows.length) return;
        const credential = useExportModal.credential;
        const payload = { title: credential.title, rows, fileName: credential.title };
        if (format === "pdf") downloadCredentialPdf(payload);
        else downloadCredentialTxt(payload);
        setExportModal(null);
    };

    const onToggleFavorite = async (credential) => {
        const previous = !!credential.isFavorite;
        const next = !previous;
        mutateLocal({ id: credential.id, patch: { isFavorite: next } });

        const response = await onSilentPatch({
            endpoint: CREDENTIALS_CONSTANTS.UPDATE, id: credential.id,
            data: { isFavorite: next, accessPin: useAccessPin ?? undefined },
            errorMessage: "No se pudo guardar el destacado de la credencial.",
        });
        if (!response.success) {
            mutateLocal({ id: credential.id, patch: { isFavorite: previous } });
            return;
        }
    };

    const VISIBLE_FIELDS = onCredentialFields(FORM.values.type);

    const QUICK_ENVIRONMENT_OPTIONS = OPTIONS_ENVIRONMENT_CODE.filter((option) => option.value !== "OTHER");
    const OTHER_ENVIRONMENT_OPTION = OPTIONS_ENVIRONMENT_CODE.find((option) => option.value === "OTHER")
        ?? { value: "OTHER", label: "Otro / personalizado" };
    const ENVIRONMENT_OPTIONS = [
        { value: "", label: "Sin entorno" },
        {
            label: "Entornos rápidos",
            options: QUICK_ENVIRONMENT_OPTIONS.map((option) => ({
                value: `preset:${option.value}`,
                label: option.label,
            })),
        },
        ...(ENVIRONMENTS.length ? [{
            label: "Entornos creados en el proyecto",
            options: ENVIRONMENTS.map((environment) => ({
                value: environment.id,
                label: `${environment.subProjectName} · ${environment.name} (${onFindOptionLabel(OPTIONS_ENVIRONMENT_CODE, environment.code)})`,
            })),
        }] : []),
    ];

    const HAS_ENVIRONMENT_OPTION = ENVIRONMENT_OPTIONS.flatMap((option) => option.options ?? [option])
        .some((option) => String(option.value) === String(FORM.values.environmentId));
    if (FORM.values.environmentId && !HAS_ENVIRONMENT_OPTION && FORM.values.environmentId !== "preset:OTHER") {
        const stale = useCredentialEdit?.environment;
        ENVIRONMENT_OPTIONS.push({
            value: FORM.values.environmentId,
            label: stale?.name ? `${stale.subProjectName ? `${stale.subProjectName} · ` : ""}${stale.name}` : "Entorno guardado",
        });
    }
    ENVIRONMENT_OPTIONS.push({
        value: `preset:${OTHER_ENVIRONMENT_OPTION.value}`,
        label: OTHER_ENVIRONMENT_OPTION.label,
    });

    const EDIT_HAS_CHILDREN = (useCredentialEdit?.children ?? []).length > 0;
    const PARENT_OPTIONS = [
        { value: "", label: "Nivel principal (sin padre)" },
        ...(EDIT_HAS_CHILDREN ? [] : TREE
            .filter((credential) => String(credential.id) !== String(useCredentialEdit?.id ?? ""))
            .map((credential) => ({
                value: credential.id,
                label: credential.title,
            }))),
    ];

    const ITEMS = CREDENTIALS ?? [];
    const SECRET_PLACEHOLDER = useCredentialEdit && !useRevealed[useCredentialEdit.id]
        ? "Valor guardado · revelá para editar"
        : "";
    const HAS_STORED_SECRETS = !!useCredentialEdit && [
        useCredentialEdit.hasSecret, useCredentialEdit.hasToken,
        useCredentialEdit.hasConnectionString, useCredentialEdit.hasSshKey,
        ...(useCredentialEdit.extra ?? []).map((field) => !!field.isSecret),
    ].some(Boolean);

    const onRevealForEdit = async () => {
        if (!useCredentialEdit) return;
        const data = await onRevealData(useCredentialEdit);
        if (!data) return;
        FORM.setValues({
            ...FORM.values,
            secret: data.secret ?? "",
            passwordHint: data.passwordHint ?? "",
            token: data.token ?? "",
            connectionString: data.connectionString ?? "",
            sshKey: data.sshKey ?? "",
        });
        const SPLIT_EXTRA = onSplitCredentialTechnicalExtra(data.extra ?? useCredentialEdit.extra ?? []);
        setExtra(SPLIT_EXTRA.custom);
        setTechnical(SPLIT_EXTRA.technical);
    };

    const onRenderNestedCredentialCard = (credential) => {
        const REVEALED = useRevealed[credential.id];
        const IS_EXPANDED = !!useCardExpanded[credential.id];
        const STANDARD_FIELDS = [...onCredentialFields(credential.type), "sshKey"];
        const PRIMARY_FIELDS = STANDARD_FIELDS.slice(0, 5);
        const DETAIL_FIELDS = STANDARD_FIELDS.slice(5);
        const HAS_DETAILS = DETAIL_FIELDS.length > 0
            || (credential.extra ?? []).length > 0
            || !!credential.sshCommand || !!credential.sshKeygenCommand
            || (credential.files ?? []).length > 0 || !!credential.notes;
        const PARENT_TITLE = NESTED_CREDENTIAL?.title || "Credencial principal";

        return (
            <Card key={credential.id}
                className="LStudio__app_credentials_item LStudio__app_credentials_nested_card"
                data-level={1}
                data-favorite={!!credential.isFavorite}>

                <div className="LStudio__app_credentials_nested_context">
                    <span className="LStudio__app_credentials_nested_branch" aria-hidden="true">↳</span>
                    <span className="LStudio__app_credentials_nested_parent">
                        Hija de <strong>{PARENT_TITLE}</strong>
                    </span>
                    <Badge tone="neutral" size="sm">Credencial hija</Badge>
                </div>

                <div className="LStudio__app_credentials_item_head" data-credential-focus-id={credential.id}>
                    {credential.isFavorite && <FaStar className="LStudio__app_credentials_item_star" title="Destacada" />}
                    <span className="LStudio__app_credentials_item_title">{credential.title}</span>

                    <Badge tone="neutral" size="sm">
                        {onFindOptionLabel(OPTIONS_CREDENTIAL_TYPE, credential.type)}
                    </Badge>

                    {credential.environment && (
                        <Badge tone="accent" size="sm">
                            {credential.environment.subProjectName ? `${credential.environment.subProjectName} · ` : ""}{credential.environment.name}
                        </Badge>
                    )}


                    <CardActionMenu
                        title="Acciones de credencial"
                        ariaLabel={`Opciones de ${credential.title}`}
                        popoverClassName="LStudio__app_credentials_nested_popover"
                        items={[
                            {
                                key: "favorite",
                                icon: credential.isFavorite ? <FaRegStar /> : <FaStar />,
                                label: credential.isFavorite ? "Quitar destacado" : "Destacar",
                                onClick: () => onToggleFavorite(credential),
                            },
                            { key: "export", icon: <FaFileExport />, label: "Guardar credencial…", onClick: () => onOpenExport(credential) },
                            { divider: true },
                            { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(credential) },
                            { divider: true },
                            { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive(credential) },
                        ]}
                    />
                </div>

                <div className="LStudio__app_credentials_item_body">
                    {credential.envKey && (
                        <div className="LStudio__app_credentials_item_row">
                            <span className="LStudio__app_credentials_item_key">Prefijo .env</span>
                            <CopyValue value={credential.envKey} label="Copiar prefijo .env" />
                        </div>
                    )}
                    {PRIMARY_FIELDS.map((field) => onRenderRow(credential, field))}
                </div>

                {IS_EXPANDED && (
                    <div className="LStudio__app_credentials_details">
                        <div className="LStudio__app_credentials_item_body">
                            {DETAIL_FIELDS.map((field) => onRenderRow(credential, field))}

                            {(credential.extra ?? []).map((field, index) => {
                                const value = field.isSecret && REVEALED
                                    ? (REVEALED.extra?.find((item) => item.label === field.label)?.value ?? field.value)
                                    : field.value;
                                return (
                                    <div key={`${field.label}_${index}`} className="LStudio__app_credentials_item_row">
                                        <span className="LStudio__app_credentials_item_key">{field.label}</span>
                                        {!field.isSecret || REVEALED
                                            ? <CopyValue value={value} label={`Copiar ${field.label}`} />
                                            : <span className="LStudio__app_credentials_item_hidden">{field.value}</span>}
                                    </div>
                                );
                            })}
                        </div>

                        {credential.sshCommand && (
                            <div className="LStudio__app_credentials_command">
                                <AppCodeBlock code={credential.sshCommand} language="bash" title="Conexión SSH" isPretty={false} />
                            </div>
                        )}

                        {credential.sshKeygenCommand && (
                            <div className="LStudio__app_credentials_command">
                                <AppCodeBlock code={credential.sshKeygenCommand} language="bash" title="Generador de clave SSH" isPretty={false} />
                            </div>
                        )}

                        {(credential.files ?? []).length > 0 && (
                            <div className="LStudio__app_credentials_files LStudio__app_file_collection">
                                <div className="LStudio__app_file_collection_header">
                                    <span>{(credential.files ?? []).length} archivo{(credential.files ?? []).length === 1 ? "" : "s"}</span>
                                    <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                                        isLoading={useDownloadingFilesKey === `credential:${credential.id}`}
                                        onClick={() => onDownloadCredentialFiles(credential)}>
                                        Descargar todos
                                    </AppButton>
                                </div>
                                <div className="LStudio__app_file_collection_scroll">
                                    {(credential.files ?? []).map((file) => (
                                        <div key={file.id} className="LStudio__app_credentials_file">
                                            <FaFileAlt />
                                            <span className="LStudio__app_credentials_file_name">
                                                <strong>{FILE_PURPOSE_LABEL[file.purpose] || "Archivo"}</strong>
                                                <small>{file.originalName || file.title}</small>
                                            </span>
                                            <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaExpand />}
                                                aria-label="Vista previa" title="Vista previa" onClick={() => setPreviewFile(file)} />
                                            <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaDownload />}
                                                aria-label="Descargar archivo" title="Descargar archivo" onClick={() => onDownloadCredentialFile(file)} />
                                            <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                                aria-label="Archivar archivo" title="Archivar archivo" onClick={() => onArchiveCredentialFile(file)} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {credential.notes && <p className="LStudio__app_credentials_item_notes">{onStripHtml(credential.notes)}</p>}
                    </div>
                )}

                <div className="LStudio__app_credentials_item_actions">
                    <AppButton size="sm" variant="ghost" tone="neutral"
                        icon={REVEALED ? <FaEyeSlash /> : <FaEye />}
                        onClick={() => onReveal(credential)}>
                        {REVEALED ? "Ocultar" : "Revelar"}
                    </AppButton>

                    {HAS_DETAILS && (
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            icon={IS_EXPANDED ? <FaChevronDown /> : <FaChevronRight />}
                            onClick={() => onToggleCardExpanded(credential.id)}>
                            {IS_EXPANDED ? "Mostrar menos" : "Mostrar más"}
                        </AppButton>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <>
            {project.hasAccessPin && (
                <p className="LStudio__app_credentials_lock_row">
                    <span className="LStudio__app_credentials_lock" data-locked={!useAccessPin}>
                        <span className="LStudio__app_credentials_lock_icon">{useAccessPin ? <FaLockOpen /> : <FaLock />}</span>
                        <span>
                            <strong>{useAccessPin ? "Desbloqueado" : "Bloqueado"}</strong>
                            <small>{useAccessPin ? "Credenciales habilitadas con el PIN del proyecto" : "Ingresá el PIN del proyecto para acceder"}</small>
                        </span>
                    </span>
                </p>
            )}

            <div className="LStudio__app_panel_bar">
                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch}
                            total={ITEMS.length} matches={SEARCH_ITEMS.length}
                            placeholder="Ej.: GitLab, usuario admin o producción..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {ADVANCED_COUNT ? `Avanzado (${ADVANCED_COUNT})` : "Avanzado"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={ADVANCED_COUNT}
                        onClear={() => { setTypeFilter("ALL"); setEnvironmentFilter("ALL"); setFavoriteFilter("ALL"); }}>
                        <FieldSelect id="credentials_adv_type" label="Tipo" value={useTypeFilter} onChange={(event) => setTypeFilter(event.target.value)} options={[{ value: "ALL", label: "Todos los tipos" }, ...OPTIONS_CREDENTIAL_TYPE]} isSearchable />
                        <FieldSelect id="credentials_adv_environment" label="Entorno" value={useEnvironmentFilter} onChange={(event) => setEnvironmentFilter(event.target.value)} options={[{ value: "ALL", label: "Todos los entornos" }, ...ENVIRONMENTS.map((environment) => ({ value: environment.id, label: `${environment.subProjectName} · ${environment.name}` }))]} isSearchable />
                        <FieldSelect id="credentials_adv_favorite" label="Destacado" value={useFavoriteFilter} onChange={(event) => setFavoriteFilter(event.target.value)} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                    </AdvancedFilterPanel>}
                    total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Credencial" },
                        { key: "type", label: "Tipo" },
                        { key: "envKey", label: "Prefijo .env" },
                        { key: "email", label: "Correo" },
                        { key: "username", label: "Usuario" },
                        { key: "host", label: "Host" },
                        { key: "environment", label: "Entorno" },
                    ]}
                    exportRows={SEARCH_ITEMS.map((credential) => ({
                        title: credential.title,
                        type: onFindOptionLabel(OPTIONS_CREDENTIAL_TYPE, credential.type),
                        envKey: credential.envKey ?? "",
                        email: credential.email ?? "",
                        username: credential.username ?? "",
                        host: credential.host ?? "",
                        environment: credential.environment?.name ?? "",
                    }))}
                    exportName="credenciales"
                    extraMenuItems={ENVIRONMENTS.map((environment) => ({
                        key: `env_${environment.id}`,
                        icon: <FaFileExport />,
                        label: `Descargar .env · ${environment.subProjectName} · ${environment.name}`,
                        onClick: () => onExportEnv(environment.id),
                    }))}
                    actions={<AppButton icon={<FaPlus />} onClick={() => onOpenCreate()}>Nueva credencial</AppButton>} />
            </div>

            {useExported !== null && (
                <div className="LStudio__app_credentials_export">
                    <p className="LStudio__app_credentials_export_hint">
                        Cada bloque lleva el prefijo de su credencial. Para cambiarlo, editá la credencial
                        y tocá el campo "Prefijo para el .env".
                    </p>
                    <AppCodeBlock code={useExported} language="text" title="Exportación .env" isPretty={false} />
                    <AppButton size="sm" variant="ghost" tone="neutral" onClick={() => setExported(null)}>Cerrar</AppButton>
                </div>
            )}

            {IS_LOADING && <Skeleton variant="box" height={140} />}

            {!IS_LOADING && ITEMS.length === 0 && (
                <EmptyState icon={<FaKey />} title="Sin credenciales guardadas"
                    description="Podés anidarlas, agregar campos propios, comandos SSH y archivos de claves." />
            )}

            {!IS_LOADING && ITEMS.length > 0 && DISPLAY_ITEMS.length === 0 && (
                <EmptyState icon={<FaKey />} title="No encontramos credenciales"
                    description="Probá con otro título, tipo, usuario, host, SSH o entorno." />
            )}

            {!IS_LOADING && DISPLAY_ITEMS.length > 0 && (
                <div className="LStudio__app_credentials_grid">
                    {PAGER.paged.map((credential) => {
                        const REVEALED = useRevealed[credential.id];
                        const IS_EXPANDED = !!useCardExpanded[credential.id];
                        const STANDARD_FIELDS = [...onCredentialFields(credential.type), "sshKey"];
                        const PRIMARY_FIELDS = STANDARD_FIELDS.slice(0, 5);
                        const DETAIL_FIELDS = STANDARD_FIELDS.slice(5);
                        const HAS_DETAILS = DETAIL_FIELDS.length > 0
                            || (credential.extra ?? []).length > 0
                            || !!credential.sshCommand || !!credential.sshKeygenCommand
                            || (credential.files ?? []).length > 0 || !!credential.notes;

                        return (
                            <Card key={credential.id} className="LStudio__app_credentials_item"
                                data-level={0}
                                data-favorite={!!credential.isFavorite}>

                                <div className="LStudio__app_credentials_item_head" data-credential-focus-id={credential.id}>
                                    {credential.isFavorite && <FaStar className="LStudio__app_credentials_item_star" title="Destacada" />}
                                    <span className="LStudio__app_credentials_item_title">{credential.title}</span>

                                    <Badge tone="neutral" size="sm">
                                        {onFindOptionLabel(OPTIONS_CREDENTIAL_TYPE, credential.type)}
                                    </Badge>

                                    {credential.environment && (
                                        <Badge tone="accent" size="sm">
                                            {credential.environment.subProjectName ? `${credential.environment.subProjectName} · ` : ""}{credential.environment.name}
                                        </Badge>
                                    )}

                                    {credential.children?.length > 0 && (
                                        <Badge tone="neutral" size="sm">
                                            <FaSitemap /> {credential.children.length} hijas
                                        </Badge>
                                    )}

                                    <CardActionMenu
                                        title="Acciones de credencial"
                                        ariaLabel={`Opciones de ${credential.title}`}
                                        items={[
                                            {
                                                key: "favorite",
                                                icon: credential.isFavorite ? <FaRegStar /> : <FaStar />,
                                                label: credential.isFavorite ? "Quitar destacado" : "Destacar",
                                                onClick: () => onToggleFavorite(credential),
                                            },
                                            { key: "export", icon: <FaFileExport />, label: "Guardar credencial…", onClick: () => onOpenExport(credential) },
                                            { divider: true },
                                            ...(credential.children?.length ? [{ key: "nested", icon: <FaSitemap />, label: `Ver hijas (${credential.children.length})`, onClick: () => setNestedCredential(credential.id) }] : []),
                                            { key: "child", icon: <FaPlus />, label: "Agregar hija", onClick: () => onOpenCreate(credential.id) },
                                            { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(credential) },
                                            { divider: true },
                                            { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive(credential) },
                                        ]}
                                    />
                                </div>

                                <div className="LStudio__app_credentials_item_body">
                                    {credential.envKey && (
                                        <div className="LStudio__app_credentials_item_row">
                                            <span className="LStudio__app_credentials_item_key">Prefijo .env</span>
                                            <CopyValue value={credential.envKey} label="Copiar prefijo .env" />
                                        </div>
                                    )}
                                    {PRIMARY_FIELDS.map((field) => onRenderRow(credential, field))}
                                </div>

                                {IS_EXPANDED && (
                                    <div className="LStudio__app_credentials_details">
                                        <div className="LStudio__app_credentials_item_body">
                                            {DETAIL_FIELDS.map((field) => onRenderRow(credential, field))}

                                            {(credential.extra ?? []).map((field, index) => {
                                                const value = field.isSecret && REVEALED
                                                    ? (REVEALED.extra?.find((item) => item.label === field.label)?.value ?? field.value)
                                                    : field.value;
                                                return (
                                                    <div key={`${field.label}_${index}`} className="LStudio__app_credentials_item_row">
                                                        <span className="LStudio__app_credentials_item_key">{field.label}</span>
                                                        {!field.isSecret || REVEALED
                                                            ? <CopyValue value={value} label={`Copiar ${field.label}`} />
                                                            : <span className="LStudio__app_credentials_item_hidden">{field.value}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {credential.sshCommand && (
                                            <div className="LStudio__app_credentials_command">
                                                <AppCodeBlock code={credential.sshCommand} language="bash" title="Conexión SSH" isPretty={false} />
                                            </div>
                                        )}

                                        {credential.sshKeygenCommand && (
                                            <div className="LStudio__app_credentials_command">
                                                <AppCodeBlock code={credential.sshKeygenCommand} language="bash" title="Generador de clave SSH" isPretty={false} />
                                            </div>
                                        )}

                                        {(credential.files ?? []).length > 0 && (
                                            <div className="LStudio__app_credentials_files LStudio__app_file_collection">
                                                <div className="LStudio__app_file_collection_header">
                                                    <span>{(credential.files ?? []).length} archivo{(credential.files ?? []).length === 1 ? "" : "s"}</span>
                                                    <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                                                        isLoading={useDownloadingFilesKey === `credential:${credential.id}`}
                                                        onClick={() => onDownloadCredentialFiles(credential)}>
                                                        Descargar todos
                                                    </AppButton>
                                                </div>
                                                <div className="LStudio__app_file_collection_scroll">
                                                    {(credential.files ?? []).map((file) => (
                                                        <div key={file.id} className="LStudio__app_credentials_file">
                                                            <FaFileAlt />
                                                            <span className="LStudio__app_credentials_file_name">
                                                                <strong>{FILE_PURPOSE_LABEL[file.purpose] || "Archivo"}</strong>
                                                                <small>{file.originalName || file.title}</small>
                                                            </span>
                                                            <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaExpand />}
                                                                aria-label="Vista previa" title="Vista previa" onClick={() => setPreviewFile(file)} />
                                                            <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaDownload />}
                                                                aria-label="Descargar archivo" title="Descargar archivo" onClick={() => onDownloadCredentialFile(file)} />
                                                            <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                                                aria-label="Archivar archivo" title="Archivar archivo" onClick={() => onArchiveCredentialFile(file)} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {credential.notes && <p className="LStudio__app_credentials_item_notes">{onStripHtml(credential.notes)}</p>}
                                    </div>
                                )}

                                <div className="LStudio__app_credentials_item_actions">
                                    <AppButton size="sm" variant="ghost" tone="neutral"
                                        icon={REVEALED ? <FaEyeSlash /> : <FaEye />}
                                        onClick={() => onReveal(credential)}>
                                        {REVEALED ? "Ocultar" : "Revelar"}
                                    </AppButton>

                                    {HAS_DETAILS && (
                                        <AppButton size="sm" variant="ghost" tone="neutral"
                                            icon={IS_EXPANDED ? <FaChevronDown /> : <FaChevronRight />}
                                            onClick={() => onToggleCardExpanded(credential.id)}>
                                            {IS_EXPANDED ? "Mostrar menos" : "Mostrar más"}
                                        </AppButton>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <NestedVaultModal
                isOpen={!!NESTED_CREDENTIAL}
                onClose={() => setNestedCredential(null)}
                title={NESTED_CREDENTIAL ? `Bóveda de credenciales · ${NESTED_CREDENTIAL.title}` : "Bóveda de credenciales"}
                description="Una credencial principal puede tener varias hijas. Las hijas no pueden contener otras credenciales."
                parentTitle={NESTED_CREDENTIAL?.title || "Credencial principal"}
                parentLabel="Credencial principal"
                childLabel="hija"
                childCount={(NESTED_CREDENTIAL?.children ?? []).length}
                addLabel="Agregar hija"
                onAddChild={NESTED_CREDENTIAL ? () => onOpenCreate(NESTED_CREDENTIAL.id) : undefined}
                isChildFormOpen={useIsModalOpen}>
                {(NESTED_CREDENTIAL?.children ?? []).map((nested) => onRenderNestedCredentialCard(nested))}
            </NestedVaultModal>

            <UniversalFilePreviewModal
                file={usePreviewFile}
                isOpen={!!usePreviewFile}
                onClose={() => setPreviewFile(null)}
                loadBlob={onLoadCredentialFileBlob}
                onDownload={onDownloadCredentialFile}
            />

            <AppModal isOpen={!!useExportModal} onClose={() => setExportModal(null)} size="md"
                title="Guardar / exportar credencial"
                description="La pista de contraseña queda marcada para compartir. La contraseña real y los demás secretos permanecen desmarcados hasta que decidas incluirlos."
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={() => setExportModal(null)}>Cancelar</AppButton>
                        <AppButton variant="outline" tone="neutral" icon={<FaFileAlt />}
                            isDisabled={!Object.values(useExportSelection).some(Boolean)}
                            onClick={() => onExportCredential("txt")}>Descargar TXT</AppButton>
                        <AppButton icon={<FaFilePdf />}
                            isDisabled={!Object.values(useExportSelection).some(Boolean)}
                            onClick={() => onExportCredential("pdf")}>Descargar PDF</AppButton>
                    </>
                }>
                <div className="LStudio__app_credentials_export_picker">
                    <div className="LStudio__app_credentials_export_picker_actions">
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            onClick={() => setExportSelection(Object.fromEntries((useExportModal?.rows ?? []).map((row) => [row.key, true])))}>
                            Todo, incluidos secretos
                        </AppButton>
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            onClick={() => setExportSelection(Object.fromEntries((useExportModal?.rows ?? []).map((row) => [row.key, !row.isSecret])))}>
                            Para compartir
                        </AppButton>
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            onClick={() => setExportSelection({})}>Ninguno</AppButton>
                    </div>
                    <div className="LStudio__app_credentials_export_picker_list">
                        {(useExportModal?.rows ?? []).map((row) => (
                            <label key={row.key} className="LStudio__app_credentials_export_picker_item">
                                <input type="checkbox" checked={!!useExportSelection[row.key]}
                                    onChange={(event) => setExportSelection((prev) => ({ ...prev, [row.key]: event.target.checked }))} />
                                <span>{row.label}</span>
                                {row.isSecret && <Badge tone="warn" size="sm">Sensible</Badge>}
                            </label>
                        ))}
                    </div>
                </div>
            </AppModal>

            <AppModal isOpen={useIsModalOpen} onClose={onCloseModal}
                className={NESTED_CREDENTIAL ? "LStudio__app_nested_vault_child_form" : ""}
                isDirty={FORM.isDirty || useExtra.length > 0 || usePublicKeyFiles.length > 0 || usePrivateKeyFiles.length > 0}
                size="xl"
                title={useCredentialEdit ? "Editar credencial" : "Nueva credencial"}
                description={
                    useCredentialEdit && !useRevealed[useCredentialEdit.id]
                        ? "Los secretos se mantienen si los dejás vacíos. Revelá la credencial antes de quitar Cifrar en un campo propio."
                        : "Contraseña, pista, token, cadena de conexión, clave SSH y campos marcados como secretos se guardan cifrados."
                }
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={onCloseModal}>Cancelar</AppButton>
                        <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                            {useCredentialEdit ? "Guardar cambios" : "Guardar credencial"}
                        </AppButton>
                    </>
                } >

                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="credential_isFavorite" label="Destacado" text="Mostrar esta credencial primero"
                        isChecked={!!FORM.values.isFavorite}
                        onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
                </div>

                <FormSection title="Identificación">
                    <FormGrid columns={3}>
                        <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: DB PRODUCCIÓN" />
                        <FieldSelect {...FORM.field("type")} label="Tipo" options={OPTIONS_CREDENTIAL_TYPE} isSearchable />
                        <FieldSelect {...FORM.field("environmentId")} label="Entorno" options={ENVIRONMENT_OPTIONS} isSearchable
                            hint="Podés elegir un entorno rápido (Local, QA, Staging, Producción, etc.) o uno creado dentro del proyecto." />

                        <FieldInput {...FORM.field("envKey")} label="Prefijo para el .env" placeholder="Ej: DB_PROD"
                            hint="Vacío lo deriva automáticamente del título." />

                        <FieldSelect {...FORM.field("parentId")} label="Credencial padre" options={PARENT_OPTIONS} isSearchable
                            hint={EDIT_HAS_CHILDREN ? "Esta credencial ya es padre y debe permanecer en el nivel principal." : "Opcional. Sólo se permite un nivel: principal → hijas."} span={2} />
                    </FormGrid>
                </FormSection>

                {useCredentialEdit && HAS_STORED_SECRETS && !useRevealed[useCredentialEdit.id] && (
                    <div className="LStudio__app_credentials_secret_edit_notice">
                        <div>
                            <strong>Esta credencial tiene valores sensibles guardados.</strong>
                            <span>Se conservan aunque los inputs aparezcan vacíos. Revelalos sólo si necesitás verlos o modificarlos.</span>
                        </div>
                        <AppButton size="sm" variant="soft" tone="neutral" icon={<FaEye />} onClick={onRevealForEdit}>
                            Revelar secretos para editar
                        </AppButton>
                    </div>
                )}

                <CredentialSpecializedSections
                    idPrefix="project_credential"
                    type={FORM.values.type}
                    form={FORM}
                    technical={useTechnical}
                    onTechnicalChange={onChangeTechnical}
                    secretPlaceholder={SECRET_PLACEHOLDER}
                    secretHints={{
                        secret: useCredentialEdit?.hasSecret && !useRevealed[useCredentialEdit.id] ? "Hay una contraseña cifrada guardada. Vacío = conservar." : undefined,
                        token: useCredentialEdit?.hasToken && !useRevealed[useCredentialEdit.id] ? "Hay un token cifrado guardado. Vacío = conservar." : undefined,
                        connectionString: useCredentialEdit?.hasConnectionString && !useRevealed[useCredentialEdit.id] ? "Hay una cadena cifrada guardada. Vacío = conservar." : undefined,
                    }}
                    visibleFields={VISIBLE_FIELDS}
                    includeUrl={false}
                    renderSshKey={false}
                />

                <FormSection title="SSH y acceso remoto"
                    description="Completá arriba Host/IP, Usuario y, si corresponde, Puerto. Después presioná Generar conexión SSH para construir el comando automáticamente. También podés pegar claves o guardar los archivos público/privado cifrados.">
                    <FormGrid columns={2}>
                        <FieldCode {...FORM.field("sshCommand")} label="Comando de conexión SSH" language="bash" rows={3}
                            placeholder="ssh -p 22 usuario@servidor" />
                        <div className="LStudio__app_credentials_generator_action">
                            <AppButton size="sm" variant="soft" tone="neutral" icon={<FaTerminal />} onClick={onGenerateSshCommand}>
                                Generar conexión SSH
                            </AppButton>
                        </div>

                        <FieldCode {...FORM.field("sshKeygenCommand")} label="Comando generador de clave SSH" language="bash" rows={3}
                            placeholder="ssh-keygen -t ed25519 ..." />
                        <div className="LStudio__app_credentials_generator_action">
                            <AppButton size="sm" variant="soft" tone="neutral" icon={<FaTerminal />} onClick={onGenerateSshKeygen}>
                                Generar ssh-keygen
                            </AppButton>
                        </div>

                        <FieldTextarea {...FORM.field("sshKey")} label="Clave SSH pegada" rows={5}
                            placeholder={SECRET_PLACEHOLDER || "Pegá una clave o hash SSH. Se guarda cifrado."} span={2} />

                        <FieldFile id="credential_public_key" label="Archivo de clave pública" files={usePublicKeyFiles}
                            onChange={setPublicKeyFiles} maxFiles={1} maxSize={2 * 1024 * 1024}
                            dropText="Ej: id_ed25519.pub" span={1} />

                        <FieldFile id="credential_private_key" label="Archivo de clave privada" files={usePrivateKeyFiles}
                            onChange={setPrivateKeyFiles} maxFiles={1} maxSize={2 * 1024 * 1024}
                            dropText="Ej: id_ed25519, .pem, .key o .ppk" span={1} />
                    </FormGrid>
                </FormSection>

                <FormSection title="Campos propios" columns={1}
                    description="Agregá IDs, licencias, cuentas, rutas o cualquier valor. Activá Cifrar para los sensibles.">
                    <div className="LStudio__app_vault_extra_rows LStudio__app_credentials_extra_rows">
                        {useExtra.map((row, index) => (
                            <div key={index} className="LStudio__app_vault_extra_row LStudio__app_credentials_extra_row">
                                <div className="LStudio__app_vault_extra_row_head">
                                    <span>Campo personalizado {index + 1}</span>
                                    <AppButton size="sm" variant="ghost" tone="danger" icon={<FaTrash />} isIconOnly
                                        aria-label="Quitar campo" onClick={() => onRemoveExtra(index)} />
                                </div>
                                <FieldInput id={`credential_extra_label_${index}`} label="Nombre"
                                    placeholder="Ej: ID de cuenta" value={row.label}
                                    onChange={(event) => onChangeExtra(index, "label", event.target.value)} />
                                <FieldInput id={`credential_extra_value_${index}`} label="Valor"
                                    type={row.isSecret ? "password" : "text"} placeholder="Ej: 102"
                                    value={row.value}
                                    onChange={(event) => onChangeExtra(index, "value", event.target.value)} />
                                <div className="LStudio__app_vault_extra_secret">
                                    <FieldSwitch id={`credential_extra_secret_${index}`} label="Secreto"
                                        text="Cifrar" isChecked={row.isSecret}
                                        isDisabled={!!useCredentialEdit && row.isSecret && !useRevealed[useCredentialEdit.id]}
                                        onChange={(event) => onChangeExtra(index, "isSecret", event.target.checked)} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <AppButton className="LStudio__app_extra_add_button" size="sm" variant="soft" tone="neutral" icon={<FaPlus />} onClick={onAddExtra}>
                        Agregar campo
                    </AppButton>
                </FormSection>

                <FormSection title="Notas y aclaraciones">
                    <FormGrid columns={1}>
                        <FieldTextarea {...FORM.field("notes")} label="Notas" rows={5}
                            placeholder="Ej: Para ver las credenciales hacemos nano config/database.yml" />
                    </FormGrid>
                </FormSection>
            </AppModal>
        </>
    );
};
